import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher";
import { CONTACT_EMAIL } from "../../constants/links";
import "./Navbar.css";
import C4SEAS_Logo from "../../assets/images/mapLogo.png";
import { useAuth } from "../../context/AuthContext";

function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const panelRef = useRef(null);
  const { isAuthenticated, signOut } = useAuth();

  // (Get Started Button) Handles navigation to the Auth page and closes the mobile menu
  const handleAuthClick = () => {
    // It does 2 things:
    // A. Sends the user to the Auth page /auth
    // B. Saves where the user came from { state: { from: location } } e.g if the user was on the home page, then click on get started button the app remembers where they came from i.e home page
    navigate("/map", { state: { from: location } });
    setIsMenuOpen(false); // Closes the mobile menu
  };

  // Handle logout
  const handleLogout = async () => {
    await signOut();
    setIsMenuOpen(false);
    // Redirect to home after logout so the UI fully reflects the
    // logged-out state, especially if the user is on a protected route
    navigate("/auth");
  };

  // This useEffect runs when the user clicks on the escape key and closes the mobile menu
  useEffect(() => {
    // Function that runs whenever a keyboard key is pressed
    const onKeyDown = (e) => {
      // e = keyboard event object
      // e.key gives the name of the key that was pressed

      // Check if the pressed key is the Escape (Esc) key
      if (e.key === "Escape") {
        // Close the menu by updating the state to false
        setIsMenuOpen(false);
      }
    };

    // Add a keyboard event listener to the entire webpage
    // "keydown" means: listen when any key is pressed down
    // onKeyDown is the function that should run when the event happens
    document.addEventListener("keydown", onKeyDown);

    // Cleanup function
    // Runs automatically when the component is removed/unmounted
    return () => {
      // Remove the event listener this prevents memory leaks
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // This code listens for mouse clicks on the page and automatically closes the menu if the user clicks outside the menu panel.
  useEffect(() => {
    // Function that runs whenever the mouse is clicked
    const onPointerDown = (e) => {
      // If the menu is already closed, do nothing
      if (!isMenuOpen) return;

      // panelRef.current refers to the actual menu element in the DOM .contains(e.target) checks whether the clicked element is inside the menu panel

      // If the clicked area is NOT inside the menu panel
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        // Close the menu
        setIsMenuOpen(false);
      }
    };

    // Add a mouse event listener to the whole document
    // "mousedown" means the event fires when the mouse button is pressed
    document.addEventListener("mousedown", onPointerDown);

    // Cleanup function
    // Runs when the component unmounts
    // or before the effect runs again
    return () =>
      // Remove the event listener to avoid memory leaks
      document.removeEventListener("mousedown", onPointerDown);

    // Dependency array
    // Re-run this effect whenever isMenuOpen changes
  }, [isMenuOpen]);

  // Function that closes the mobile menu
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className="navbar">
      <div className="navbar__inner">
        <div className="navbar__left">
          {/*C4SEAS Map Logo */}
          <NavLink to="/" className="navbar__brand" aria-label={t("nav.brandAria")}>
            <img src={C4SEAS_Logo} alt={t("nav.logoAlt")} className="navbar__brandLogo" />
          </NavLink>

          <nav className="navbar__nav" aria-label="Primary navigation">
            {/* Home Link */}
            <NavLink to="/" className="navbar__link">
              {t("nav.home")}
            </NavLink>

            {/* About Link */}
            <Link to={{ pathname: "/", hash: "#about" }} className="navbar__link">
              {t("nav.about")}
            </Link>

            {/* Team Link */}
            <Link to={{ pathname: "/", hash: "#team" }} className="navbar__link">
              {t("nav.coreTeam")}
            </Link>

            {/* Partner Link */}
            <Link to={{ pathname: "/", hash: "#partners" }} className="navbar__link">
              {t("nav.partners")}
            </Link>

            {/* Contact Link */}
            <Link to={{ pathname: "/", hash: "#contact" }} className="navbar__link">
              {t("nav.contact")}
            </Link>
          </nav>
        </div>

        {/* Buttons */}
        <div className="navbar__right">
          {/* Language Switcher */}
          <LanguageSwitcher variant="navbar" />

          {/* Get Started / Logout Button */}
          <button
            type="button"
            className={`navbar__cta ${
              isAuthenticated ? "navbar__cta--logout" : ""
            }`}
            onClick={isAuthenticated ? handleLogout : handleAuthClick}
          >
            {isAuthenticated ? "Log Out" : t("nav.getStarted")}
          </button>

          {/* Mobile Menu */}
          <button
            type="button"
            className="navbar__menuButton"
            onClick={() => setIsMenuOpen((v) => !v)}
            aria-label={isMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={isMenuOpen}
            aria-controls="navbar-mobile-panel"
          >
            <span className="navbar__menuIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                {isMenuOpen ? (
                  <path
                    d="M18.3 5.71 12 12.01l-6.3-6.3-1.41 1.41 6.3 6.3-6.3 6.3 1.41 1.41 6.3-6.3 6.3 6.3 1.41-1.41-6.3-6.3 6.3-6.3z"
                    fill="currentColor"
                  />
                ) : (
                  <path d="M4 6.5h16v2H4zM4 11h16v2H4zM4 15.5h16v2H4z" fill="currentColor" />
                )}
              </svg>
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMenuOpen && <div className="navbar__backdrop" />}

      {/* Mobile Menu Panel*/}
      <div
        id="navbar-mobile-panel"
        className={`navbar__mobilePanel${isMenuOpen ? " is-open" : ""}`}
        role="dialog"
        aria-label={t("nav.menu")}
        ref={panelRef}
      >
        <nav className="navbar__mobileNav" aria-label={t("nav.mobileNavAria")}>
          {/* Home Link */}
          <NavLink to="/" className="navbar__mobileLink" onClick={closeMenu}>
            {t("nav.home")}
          </NavLink>

          {/* About Link */}
          <Link
            to={{ pathname: "/", hash: "#about" }}
            className="navbar__mobileLink"
            onClick={closeMenu}
          >
            {t("nav.about")}
          </Link>

          {/* Team Link */}
          <Link
            to={{ pathname: "/", hash: "#team" }}
            className="navbar__mobileLink"
            onClick={closeMenu}
          >
            {t("nav.coreTeam")}
          </Link>

          {/* Partner Link */}
          <Link
            to={{ pathname: "/", hash: "#partners" }}
            className="navbar__mobileLink"
            onClick={closeMenu}
          >
            {t("nav.partners")}
          </Link>

          {/* Gmail */}
          <a className="navbar__mobileLink" href={`mailto:${CONTACT_EMAIL}`} onClick={closeMenu}>
            {t("nav.contact")}
          </a>
        </nav>

        {/* Get Started / Logout Button */}
        <div className="navbar__mobileActions">
          <button
            type="button"
            className={`navbar__mobileCta ${
              isAuthenticated ? "navbar__mobileCta--logout" : ""
            }`}
            onClick={isAuthenticated ? handleLogout : handleAuthClick}
          >
            {isAuthenticated ? "Log Out" : t("nav.getStarted")}
          </button>
        </div>
      </div>
    </header>
  );
}

export default Navbar;